import React, { useState, useEffect } from 'react';
import { X, ChefHat, Save } from 'lucide-react';

const NOTE_EXAMPLES = [
  'Rush order - customer in hurry',
  'Prepare together and serve at once',
  'Customer allergic to nuts',
  'VIP guest - special attention',
  'Birthday celebration',
  'Surprise dessert at end',
];

export default function OrderNotesModal({ currentNotes, onSave, onClose }) {
  const [notes, setNotes] = useState(currentNotes || '');

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        handleSave();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [notes]);

  const handleSave = () => {
    onSave(notes.trim());
  };

  // Parse the textarea into individual note "chips" so multiple quick-notes
  // can be selected at once instead of one replacing the whole field.
  const selectedParts = notes
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  const isExampleSelected = (example) => selectedParts.includes(example);

  const toggleExample = (example) => {
    if (isExampleSelected(example)) {
      // Remove just this phrase, keep everything else (including free text)
      const updated = selectedParts.filter((p) => p !== example);
      setNotes(updated.join(', '));
    } else {
      // Append this phrase alongside whatever is already selected/typed
      const updated = [...selectedParts, example];
      setNotes(updated.join(', '));
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg transform animate-slideUp">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-t-2xl p-6">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
          >
            <X size={20} />
          </button>

          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
              <ChefHat size={24} />
            </div>
            <div>
              <h3 className="text-xl font-bold">Order Notes</h3>
              <p className="text-sm text-white/80">Instructions for the entire order</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Note Examples - Multi-select chips */}
          <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-teal-900 mb-2">💡 Common Notes (tap to add or remove):</p>
            <div className="flex flex-wrap gap-2">
              {NOTE_EXAMPLES.map((example) => {
                const selected = isExampleSelected(example);
                return (
                  <button
                    key={example}
                    type="button"
                    onClick={() => toggleExample(example)}
                    aria-pressed={selected}
                    className={`text-xs px-2 py-1 rounded border font-medium transition-colors flex items-center gap-1 ${
                      selected
                        ? 'bg-teal-600 border-teal-600 text-white hover:bg-teal-700'
                        : 'bg-white border-teal-300 text-teal-800 hover:bg-teal-100'
                    }`}
                  >
                    {selected && <span className="leading-none">✓</span>}
                    {example}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Text Area */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Notes for Kitchen Staff
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="E.g., Rush order, VIP guest, special attention needed..."
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none text-sm"
              rows={5}
              autoFocus
              maxLength={300}
            />
            <div className="flex items-center justify-between mt-1">
              <p className="text-xs text-slate-500">
                {notes.length}/300 characters
              </p>
              <p className="text-xs text-slate-500 italic">
                Ctrl+Enter to save
              </p>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <span className="text-lg">ℹ️</span>
              <p className="text-xs text-blue-900">
                <strong>Order-level notes</strong> apply to the entire order and will be visible to kitchen staff when this KOT is sent. These are different from item-specific special instructions.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 p-4 bg-slate-50 rounded-b-2xl flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 bg-white border-2 border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="flex-1 px-4 py-2.5 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white font-bold rounded-lg transition-all flex items-center justify-center gap-2 shadow-md"
          >
            <Save size={16} />
            Save Notes
          </button>
        </div>

        {/* Keyboard Shortcuts Info */}
        <div className="px-4 pb-3 flex items-center justify-center gap-4 text-xs text-slate-500">
          <div className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-white border border-slate-300 rounded shadow-sm font-mono text-[10px]">ESC</kbd>
            <span>Cancel</span>
          </div>
          <div className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-white border border-slate-300 rounded shadow-sm font-mono text-[10px]">Ctrl+Enter</kbd>
            <span>Save</span>
          </div>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}